(() => {
  if (customElements.get('nfc-fridge-video')) return;

  class NfcFridgeVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      this.visible = false;
      this.loopPreview = this.hasAttribute('data-loop-preview');
      this.userPaused = false;
      this.manualPlay = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.card = this.closest('.nfc-home-family--fridge-video');
      this.hovered = this.loopPreview && Boolean(this.card?.matches(':hover'));
      this.toggle = this.card?.querySelector('[data-fridge-loop-toggle]');
      const options = { signal: this.controller.signal };
      this.addEventListener('play', () => {
        if (this.loopPreview && !this.canPreview()) {
          this.resetPreview();
          return;
        }
        document.querySelectorAll('nfc-fridge-video video').forEach(video => {
          if (video !== this.video) video.pause();
        });
        this.updateToggle();
      }, { ...options, capture: true });
      this.video.addEventListener('playing', () => {
        if (!this.loopPreview) return;
        if (this.canPreview()) this.setAttribute('data-preview-active', '');
        else this.resetPreview();
      }, options);
      this.video.addEventListener('pause', () => {
        if (this.loopPreview) this.removeAttribute('data-preview-active');
        this.updateToggle();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.motion.addEventListener('change', () => {
        this.manualPlay = false;
        this.syncPlayback();
      }, options);
      if (this.loopPreview) {
        this.removeAttribute('data-preview-active');
        this.video.muted = true;
        this.video.loop = true;
        this.video.controls = false;
        this.card?.addEventListener('pointerenter', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = true;
          this.syncPlayback();
        }, options);
        this.card?.addEventListener('pointerleave', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = false;
          this.manualPlay = false;
          this.userPaused = false;
          this.resetPreview();
        }, options);
        if (this.toggle) {
          this.toggle.hidden = false;
          this.toggle.addEventListener('click', () => {
            if (this.video.paused) {
              this.userPaused = false;
              this.manualPlay = true;
            } else {
              this.userPaused = true;
              this.manualPlay = false;
            }
            this.syncPlayback();
          }, options);
        }
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25);
          if (!this.visible) this.manualPlay = false;
          this.syncPlayback();
        }, { threshold: [0, 0.25] });
        this.observer.observe(this);
      } else {
        const updateVisibility = () => {
          const bounds = this.getBoundingClientRect();
          this.visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
          this.syncPlayback();
        };
        document.addEventListener('scroll', updateVisibility, { ...options, capture: true, passive: true });
        window.addEventListener('resize', updateVisibility, options);
        updateVisibility();
      }
      this.updateToggle();
    }

    syncPlayback() {
      if (!this.controller) return;
      if (!this.visible || document.hidden) {
        if (this.loopPreview) {
          this.manualPlay = false;
          this.resetPreview();
        } else this.video.pause();
        return;
      }
      if (!this.loopPreview) return;
      if (!this.canPreview()) {
        this.resetPreview();
        return;
      }
      const detailPlaying = Array.from(document.querySelectorAll('nfc-fridge-video:not([data-loop-preview]) video')).some(video => !video.paused);
      if (detailPlaying && !this.manualPlay) return;
      if (this.video.paused) {
        const playback = this.video.play();
        playback?.catch(() => this.updateToggle());
      }
    }

    canPreview() {
      return this.visible && !document.hidden && !this.userPaused
        && (this.hovered || this.manualPlay)
        && (!(this.motion.matches || window.Shopify?.designMode) || this.manualPlay);
    }

    resetPreview() {
      this.removeAttribute('data-preview-active');
      this.video.pause();
      if (this.video.readyState > 0) this.video.currentTime = 0;
    }

    updateToggle() {
      if (!this.toggle) return;
      const paused = this.video.paused;
      this.toggle.textContent = paused ? '▶' : 'Ⅱ';
      this.toggle.setAttribute('aria-label', paused ? 'Play video preview' : 'Pause video preview');
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      if (this.loopPreview && this.video) this.resetPreview();
      else this.video?.pause();
      this.controller = null;
      if (this.toggle) this.toggle.hidden = true;
    }
  }

  customElements.define('nfc-fridge-video', NfcFridgeVideo);
})();
